import React from 'react';

import type { WidgetReference } from '@lumapps/widget-layout/types';

import { useWidget, type WidgetQueryProps } from './useWidget';
import { Widget, type WidgetProps } from './Widget';

type ForwardedProps = Pick<
    WidgetProps,
    | 'errorStateRenderer'
    | 'emptyStateRenderer'
    | 'loadingStateRenderer'
    | 'widgetContentProps'
    | 'wrapperInsideRenderer'
    | 'children'
    | 'wrapperProps'
    | 'loadingStateWrapperProps'
>;

export interface ConnectedWidgetProps<WidgetSpecificProperties = object>
    extends Omit<WidgetReference<WidgetSpecificProperties>, 'type'>,
        ForwardedProps {
    /** Exclude some properties from queryKey (to avoid unnecessary updates */
    excludedQueryKeyWidgetProperties?: Array<keyof WidgetSpecificProperties>;
    /** Optional React Query configuration to customize caching and fetching behavior */
    queryProps?: WidgetQueryProps;
}

export const ConnectedWidget: React.FC<ConnectedWidgetProps> = ({
    emptyStateRenderer,
    errorStateRenderer,
    isMainWidget,
    legacyWidget,
    loadingStateRenderer,
    widgetContentProps,
    widgetId,
    widgetType,
    children,
    wrapperInsideRenderer,
    wrapperProps,
    loadingStateWrapperProps,
    excludedQueryKeyWidgetProperties,
    queryProps,
}) => {
    const { widget, widgetRef, widgetWidth, fetchMore, refetch } = useWidget({
        widgetId,
        widgetType,
        legacyWidget,
        isMainWidget,
        excludedQueryKeyWidgetProperties,
        queryProps,
    });

    return (
        <Widget
            widget={widget}
            refetch={refetch}
            widgetContentProps={widgetContentProps}
            fetchMore={fetchMore}
            ref={widgetRef as any}
            loadingStateRenderer={loadingStateRenderer}
            emptyStateRenderer={emptyStateRenderer}
            errorStateRenderer={errorStateRenderer}
            wrapperInsideRenderer={wrapperInsideRenderer}
            widgetWidth={widgetWidth}
            wrapperProps={wrapperProps}
            loadingStateWrapperProps={loadingStateWrapperProps}
        >
            {children}
        </Widget>
    );
};
